"use client";

import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useUser } from "@clerk/nextjs";
import {
    completeSetup,
    createDefaultPreferences,
    findPreferenceModule,
    flattenHazcamsPreferences,
    isStructuredPreferences,
    normalizeStructuredPreferences,
    setPreferenceAtLocation,
} from "@/lib/preferences-schema";

const PreferencesContext = createContext();

export function usePreferences() {
    const context = useContext(PreferencesContext);
    if (!context) {
        throw new Error("usePreferences must be used within a PreferencesProvider");
    }
    return context;
}

export function PreferencesProvider({ children }) {
    const { user, isLoaded } = useUser();
    const [modulePreferences, setModulePreferences] = useState(() => createDefaultPreferences());
    const modulePreferencesRef = useRef(modulePreferences);
    const userRef = useRef(user);
    const hydratedUserIdRef = useRef(undefined);
    const saveQueueRef = useRef(Promise.resolve());
    const [loading, setLoading] = useState(true);
    const userId = user?.id || null;

    userRef.current = user;

    const persistPreferences = (preferences) => {
        const targetUserId = userRef.current?.id;
        if (!targetUserId) return Promise.resolve();

        const save = async () => {
            const currentUser = userRef.current;
            if (!currentUser || currentUser.id !== targetUserId) return;

            await currentUser.update({
                unsafeMetadata: {
                    ...currentUser.unsafeMetadata,
                    preferences,
                },
            });
        };

        // Clerk updates are serialized so a slower earlier layer change cannot
        // overwrite a newer one when several controls are changed quickly.
        saveQueueRef.current = saveQueueRef.current.then(save, save);
        return saveQueueRef.current;
    };

    const replacePreferences = async (nextPreferences) => {
        const normalized = normalizeStructuredPreferences(nextPreferences);
        modulePreferencesRef.current = normalized;
        setModulePreferences(normalized);

        try {
            await persistPreferences(normalized);
        } catch (error) {
            console.error('Error updating user preferences:', error);
        }

        return normalized;
    };

    useEffect(() => {
        if (!isLoaded) return;

        // Rehydrate only on initial load or an account change. Clerk replaces
        // the user object after metadata writes; treating that as a new source
        // briefly restores stale values and makes map layers flicker.
        if (hydratedUserIdRef.current === userId) return;
        hydratedUserIdRef.current = userId;

        const remotePreferences = userRef.current?.unsafeMetadata?.preferences;
        const source = isStructuredPreferences(remotePreferences)
            ? remotePreferences
            : createDefaultPreferences();
        const normalized = normalizeStructuredPreferences(source);

        modulePreferencesRef.current = normalized;
        setModulePreferences(normalized);
        setLoading(false);
    }, [isLoaded, userId]);

    const updateModulePreferences = async (nextPreferences) => replacePreferences(nextPreferences);

    const updatePreference = async (key, value) => {
        const location = findPreferenceModule(key);
        if (!location) {
            console.warn(`No user preference group contains "${key}".`);
            return;
        }

        const current = normalizeStructuredPreferences(modulePreferencesRef.current);
        const currentSettings = location.scope === 'user'
            ? current.user[location.moduleId]
            : current.hazcams.user[location.moduleId];
        const nextValue = typeof value === 'function' ? value(currentSettings?.[key]) : value;

        return replacePreferences(setPreferenceAtLocation(current, location, key, nextValue));
    };

    const updatePreferences = async (newPreferences) => {
        let next = modulePreferencesRef.current;
        Object.entries(newPreferences).forEach(([key, value]) => {
            const location = findPreferenceModule(key);
            if (location) next = setPreferenceAtLocation(next, location, key, value);
        });
        return replacePreferences(next);
    };

    const useDefaultPreferences = async () => {
        const defaults = createDefaultPreferences({ completeSetup: true });
        const current = normalizeStructuredPreferences(modulePreferencesRef.current);
        // Resetting HazCams must not change global user preferences or other modules.
        defaults.user = current.user;
        defaults['strike-zone'] = current['strike-zone'];
        defaults.hazalerts = current.hazalerts;
        return replacePreferences(defaults);
    };

    const finishSetup = async (nextPreferences) => replacePreferences(completeSetup(nextPreferences));
    const resetPreferences = useDefaultPreferences;
    const preferences = useMemo(
        () => flattenHazcamsPreferences(modulePreferences),
        [modulePreferences]
    );
    const setup = modulePreferences.hazcams.setup;

    const value = {
        preferences,
        modulePreferences,
        setup,
        needsSetup: !setup.completed,
        loading,
        updatePreferences,
        updatePreference,
        updateModulePreferences,
        replacePreferences,
        finishSetup,
        useDefaultPreferences,
        resetPreferences,
        ...preferences,
    };

    return (
        <PreferencesContext.Provider value={value}>
            {children}
        </PreferencesContext.Provider>
    );
}
